import React, { useMemo } from 'react'
import { Box, Paper, Grid, FormControl, InputLabel, Select, MenuItem, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { logsApi, AuditLogItem, LogsSortField } from '../api/logs'
// import { gruposApi } from '../api/grupos'
import { empresasApi } from '../api/empresas'
import { usuariosApi } from '../api/usuarios'
import { sortByLocale } from '../utils/array'
import JsonDetailDialog from '../components/common/JsonDetailDialog'
import ExportButton from '../components/ExportButton'
import ActionBar from '../components/ActionBar'

const compactFieldSx = {
  '& .MuiInputBase-root': {
    borderRadius: 1.5,
    minHeight: 28,
  },
  '& .MuiInputBase-input': {
    py: 0.25,
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.8125rem',
  },
}

const LogsPage: React.FC = () => {
  const [empresaId, setEmpresaId] = React.useState<number | ''>('')
  const [usuarioId, setUsuarioId] = React.useState<number | ''>('')
  const [actorId, setActorId] = React.useState<number | ''>('')
  const [action, setAction] = React.useState<string>('')
  const [q, setQ] = React.useState<string>('')
  const [sortBy, setSortBy] = React.useState<LogsSortField>('created_at')
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc')
  const [detailDialog, setDetailDialog] = React.useState<{ open: boolean; content?: string | null }>({ open: false, content: '' })

  const { data: empresasRaw = [] } = useQuery({ queryKey: ['empresas', 'logs'], queryFn: () => empresasApi.list() })
  const { data: usuariosRaw = [] } = useQuery({ queryKey: ['usuarios', { includeInactive: true }], queryFn: () => usuariosApi.list({ includeInactive: true }) })
  const empresas = React.useMemo(
    () => sortByLocale(Array.isArray(empresasRaw) ? empresasRaw : [], (e) => e.nome ?? ''),
    [empresasRaw],
  )
  const usuarios = React.useMemo(
    () => sortByLocale(Array.isArray(usuariosRaw) ? usuariosRaw : [], (u) => u.name ?? ''),
    [usuariosRaw],
  )

  const exportColumns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'created_at', label: 'Data' },
      { key: 'action', label: 'Ação' },
      { key: 'user_id', label: 'Usuário ID' },
      { key: 'actor_id', label: 'Ator ID' },
      { key: 'ip', label: 'IP' },
      {
        key: 'details',
        label: 'Detalhes',
        format: (value: any) => (value ? JSON.stringify(value) : ''),
      },
    ],
    [],
  )

  const { data: logs = [], isLoading, refetch } = useQuery({
    queryKey: ['logs', { empresaId, usuarioId, actorId, action, q, sortBy, sortDirection }],
    queryFn: () => logsApi.list({
      empresa_id: empresaId ? Number(empresaId) : null,
      user_id: usuarioId ? Number(usuarioId) : null,
      actor_id: actorId ? Number(actorId) : null,
      action: action || null,
      q: q || null,
      limit: 200,
      sort_by: sortBy,
      sort_direction: sortDirection,
    }),
  })

  const handleSort = (field: LogsSortField) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortDirection('desc')
    }
  }

  const openDetailDialog = (content?: string | null) => {
    setDetailDialog({ open: true, content })
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }} display="flex" flexDirection="column" gap={0}>
      <ActionBar>
        <ExportButton
          data={logs}
          columns={exportColumns}
          filename="logs.xlsx"
          sheetName="Logs"
        />
      </ActionBar>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container columnSpacing={1.5} rowSpacing={1.5}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Empresa</InputLabel>
              <Select value={empresaId} label="Empresa" onChange={(e) => setEmpresaId(e.target.value as number | '')}>
                <MenuItem value="">Todas</MenuItem>
                {empresas.map((e: any) => (<MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Usuário</InputLabel>
              <Select value={usuarioId} label="Usuário" onChange={(e) => setUsuarioId(e.target.value as number | '')}>
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map((u: any) => (<MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" margin="dense" sx={compactFieldSx}>
              <InputLabel>Ator</InputLabel>
              <Select value={actorId} label="Ator" onChange={(e) => setActorId(e.target.value as number | '')}>
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map((u: any) => (<MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Ação (prefixo)"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Busca (conteúdo)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={compactFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              onClick={() => refetch()}
              disabled={isLoading}
              sx={{ minHeight: 28, px: 2 }}
            >
              Aplicar filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'id' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'id'}
                  direction={sortBy === 'id' ? sortDirection : 'desc'}
                  onClick={() => handleSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'created_at' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'created_at'}
                  direction={sortBy === 'created_at' ? sortDirection : 'desc'}
                  onClick={() => handleSort('created_at')}
                >
                  Data
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'action' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'action'}
                  direction={sortBy === 'action' ? sortDirection : 'desc'}
                  onClick={() => handleSort('action')}
                >
                  Ação
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'user_id' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'user_id'}
                  direction={sortBy === 'user_id' ? sortDirection : 'desc'}
                  onClick={() => handleSort('user_id')}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'actor_id' ? sortDirection : false}>
                <TableSortLabel
                  active={sortBy === 'actor_id'}
                  direction={sortBy === 'actor_id' ? sortDirection : 'desc'}
                  onClick={() => handleSort('actor_id')}
                >
                  Ator
                </TableSortLabel>
              </TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((l: AuditLogItem) => (
              <TableRow key={l.id}>
                <TableCell>{l.id}</TableCell>
                <TableCell>{new Date(l.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</TableCell>
                <TableCell>{l.action}</TableCell>
                <TableCell>{l.user_id ?? '-'}</TableCell>
                <TableCell>{l.actor_id ?? '-'}</TableCell>
                <TableCell>{l.ip ?? '-'}</TableCell>
              <TableCell
                sx={{
                  maxWidth: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: l.details ? 'pointer' : 'default',
                  color: l.details ? 'primary.main' : 'text.primary',
                }}
                onClick={() => {
                  if (l.details) {
                    openDetailDialog(typeof l.details === 'object' ? JSON.stringify(l.details, null, 2) : String(l.details))
                  }
                }}
              >
                {l.details 
                  ? (typeof l.details === 'object' 
                      ? JSON.stringify(l.details).substring(0, 50) + (JSON.stringify(l.details).length > 50 ? '...' : '')
                      : String(l.details).substring(0, 50) + (String(l.details).length > 50 ? '...' : ''))
                  : '-'}
              </TableCell>
              </TableRow>
            ))}
            {(!logs || logs.length === 0) && (
              <TableRow><TableCell colSpan={7} align="center">Sem registros</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <JsonDetailDialog
        open={detailDialog.open}
        rawContent={detailDialog.content}
        title="Detalhes do log"
        onClose={() => setDetailDialog({ open: false, content: '' })}
      />
    </Box>
  )
}

export default LogsPage

